import React, { useState, useEffect, createContext } from 'react';
import { useErrorBoundary } from "react-error-boundary";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Container from '../../components/Container';
import { Topbar, Sidebar} from './components';
import Main from '../../layouts/Main';

import Box from '@mui/material/Box';
import { CircularProgress, Grid2 as Grid, Typography, ToggleButtonGroup, ToggleButton, createTheme} from '@mui/material';
import { red } from '@mui/material/colors';

import ShortFiler from '../../types/filers/shortFiler';
import FilerDocument from '../../types/filers/filerDocument';
import FilerSummary from './components/FilerSummary';
import FilerBasics from './components/FilerBasics';
import FilerPayload from '../../types/filers/filerPayload';
import FilerReports from './components/FilerReports';
import FilerDocuments from './components/FilerDocuments';
import FilerAudit from './components/FilerAudit/FilerAudit';
import FilerAuditIssue from '../../types/filers/filerAuditIssue';
import FilerContributions from './components/FilerContributions';
import FilerExpenditures from './components/FilerExpenditures';
import IndependentExpenditureReportContent from '../../types/reports/independentExpenditureReportContent';
import FilerRelatedReports from './components/FilerRelatedReports';
import FilerGeneralOverview from './FilerGeneralOverview';


export const filerTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1290,
      xl: 1536,
    },
  },
});


//useContext hook: share data across components
export interface FilersData {
  filerListing: ShortFiler[];
  currentFilerPayload: FilerPayload | null;
  currentFilerDocuments: FilerDocument[];
  currentFilerAuditIssues: FilerAuditIssue[];
  currentFilerRelatedReports: IndependentExpenditureReportContent[];
  setFilerListing: (newListing: ShortFiler[]) => void;
  setCurrentFilerPayload: (newFiler: FilerPayload) => void;
  setCurrentFilerDocuments: (newDocuments: FilerDocument[]) => void;
  setCurrentFilerAuditIssues: (newIssues: FilerAuditIssue[]) => void;
  setCurrentFilerRelatedReports: (newReports: IndependentExpenditureReportContent[]) => void;
  setContentType: (newType: string) => void;
  setContentTypes: (newTypes: string[]) => void;
  setTypesCutOffIndex: (newIndex: number) => void;
  setSideBarVariant: (newVariant: 'temporary' | 'permanent' | 'persistent') => void;
}

export const FilersDataContext = createContext<FilersData>({
  filerListing: [],
  currentFilerPayload: null,
  currentFilerDocuments: [],
  currentFilerAuditIssues: [],
  currentFilerRelatedReports: [],
  setFilerListing: () => { },
  setCurrentFilerPayload: () => { },
  setCurrentFilerDocuments: () => { },
  setCurrentFilerAuditIssues: () => { },
  setCurrentFilerRelatedReports: () => { },
  setContentType: () => { },
  setSideBarVariant: () => { },
  setContentTypes() {},
  setTypesCutOffIndex() {},
});

const Filers = (): JSX.Element => {

  document.title = 'OCPF - Registered Filers';

  //initial set up
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const isMdAndSM = useMediaQuery(theme.breakpoints.down('md'), {
    defaultMatches: true,
  });

  const isLG = useMediaQuery(filerTheme.breakpoints.up('lg'), {
    defaultMatches: true,
  });

  const isSM = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  });

  const [isLoading, setIsLoading] = useState(true);
  const { showBoundary } = useErrorBoundary();
  const [sideBarVariant, setSideBarVariant] = useState<'temporary' | 'permanent' | 'persistent'>('temporary');

  //default tab
  const defaultTabs = ['Summary', 'Reports', 'Contributions', 'Expenditures', 'Correspondence', 'Audit']
  const [contentTypes, setContentTypes] = useState<string[]>(defaultTabs);
  const [contentType, setContentType] = useState('Summary');
  const [contentTypesCutOffIndex, setTypesCutOffIndex] = useState(3); //used to cut off the tabs in the middle for small screens

  const [openSidebar, setOpenSidebar] = useState(false);
  const [filerListing, setFilerListing] = useState<ShortFiler[]>([]);
  const [currentFilerPayload, setCurrentFilerPayload] = useState<FilerPayload | null>(null);
  const [currentFilerDocuments, setCurrentFilerDocuments] = useState<FilerDocument[]>([]);
  const [currentFilerAuditIssues, setCurrentFilerAuditIssues] = useState<FilerAuditIssue[]>([]);
  const [currentFilerRelatedReports, setCurrentFilerRelatedReports] = useState<IndependentExpenditureReportContent[]>([]);

  //pass search from browser
  const queryParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    if(queryParams.has('q')){
      const cpfId = queryParams.get('q') ? parseInt(queryParams.get('q') as string, 10) : 0;
      getCurrentFiler(cpfId);
      return;
    };

    if(queryParams.has('f')){
      const cpfId = queryParams.get('f') ? parseInt(queryParams.get('f') as string, 10) : 0;
      getCurrentFiler(cpfId);
      return;
    };

    if(isMdAndSM){
      //redirect to filer listing page if no filer is selected for mobile view
      window.location.href = `/Filers/FilerListing`;
    }
    else{
      getFilerListing();
    }
  }, [])


  const getFilerListing = async () => {
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/filers/listings/C?first200=true&excludeClosed=true`)
      .then(result => result.json())
      .then(result => {
        setFilerListing(result)
        //let firstFiler = result[0];
        //let currentFilerCpfId = firstFiler? firstFiler.cpfId : 0;
        setIsLoading(false);
        let currentFilerCpfId = 0;
        if(isMd){
          setSideBarVariant('permanent');
        }
        if(queryParams.has('q')){
          currentFilerCpfId = parseInt(queryParams.get('q') || '0');
          getCurrentFiler(currentFilerCpfId);
        }
      
      });
    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const getCurrentFiler = async (cpfId: number) => {
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/filer/payload/${cpfId}`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get filer data.'));
        }
        return result.json();
      })
      .then(result => {
        setCurrentFilerPayload(result);
        setIsLoading(false);
        getCurrentFilerDocuments(cpfId);
        getCurrentFilerRelatedReports(cpfId);
        if(queryParams.has('section')){
          let currentSection = queryParams.get('section');
          setContentType(currentSection || "Summary");
        };
        if(isLG){
          setSideBarVariant('permanent');
        }
      });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
    
  }

  const getCurrentFilerDocuments = async (cpfId: number) => {
    try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/filer/correspondence/all/${cpfId}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get filer documents.'));
          }
          return result.json();
        })
        .then(result => {
            setCurrentFilerDocuments(result.documents);
            setCurrentFilerAuditIssues(result.issues);
        });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const getCurrentFilerRelatedReports = async (cpfId: number) => {
    try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/related/${cpfId}`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get filer related reports.'));
          }
          return result.json();
        })
        .then(result => {
            if(result.length > 0){
              setCurrentFilerRelatedReports(result);
              setContentTypes([...defaultTabs, 'Related Reports']);
              setTypesCutOffIndex(4);
            }
            else{
              setContentTypes(defaultTabs);
            }
        });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
  }

  const switchContentType = (event: any, newType: string) => {
    if (newType !== null) {
      setContentType(newType);
    }
  };

  const handleSidebarOpen = (): void => {
    setOpenSidebar(true);
    if(isLG){
      setSideBarVariant('permanent');
    }
  };

  const handleSidebarClose = (): void => {
    setOpenSidebar(false);
  };

  const isSideBarOpen = (isLG) ? false : openSidebar;

  return (
    <Main>
      <FilersDataContext.Provider value={{
              filerListing: filerListing,
              currentFilerPayload: currentFilerPayload,
              currentFilerDocuments: currentFilerDocuments,
              currentFilerAuditIssues: currentFilerAuditIssues,
              currentFilerRelatedReports: currentFilerRelatedReports,
              setFilerListing: setFilerListing,
              setCurrentFilerPayload: setCurrentFilerPayload,
              setCurrentFilerDocuments: setCurrentFilerDocuments,
              setCurrentFilerAuditIssues: setCurrentFilerAuditIssues,
              setCurrentFilerRelatedReports: setCurrentFilerRelatedReports,
              setContentType: setContentType,
              setContentTypes: setContentTypes,
              setTypesCutOffIndex: setTypesCutOffIndex,
              setSideBarVariant: setSideBarVariant,
            }}>
        <Box>
          <Sidebar
            onClose={handleSidebarClose}
            open={isSideBarOpen}
            //variant={(isLG || !currentFilerPayload) ? 'permanent' : 'temporary'}
            variant={sideBarVariant}
            onHide={()=>setSideBarVariant('temporary')}
          />
          <main>
            {/*side bar*/}
            <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
              <Topbar hideTopBar={isLG && sideBarVariant==="permanent"} onSidebarOpen={handleSidebarOpen} />
            </Container>
             {/*filer main page*/}
             { !isLoading && currentFilerPayload?.filer && currentFilerPayload.filer.cpfId > 0 &&
                <Box
                   display="flex"
                   flex="1 1 auto"
                   overflow="hidden"
                   paddingLeft={(isLG && sideBarVariant==="permanent")? '275px': isLG?'80px':'20px'}
                   paddingTop={1}
                   >
                  <Box display="flex" flex="1 1 auto" overflow="hidden">
                    <Box flex="1 1 auto" height="100%" overflow="hidden">
                      <Box p={2} paddingTop={1}>
                       <Box
                         width={1}
                         height={1}
                         minHeight={'calc(100vh)'}
                         borderRadius={2}
                       >
                       {/*filer basic info*/}
                         <Grid>
                          <Typography
                            variant="h5"
                            align={'left'}
                            gutterBottom
                            fontWeight={700}
                            paddingBottom={1.5}
                            color={
                              currentFilerPayload?.filer.isActive
                                ? 'inherit'
                                : red[500]
                            }
                            >
                             {
                                contentType !== 'Summary' &&
                                <>
                                  {currentFilerPayload?.filer.cpfId} {currentFilerPayload?.filer.fullNameReverse}
                                </>
                             }
                          </Typography>
                         </Grid>
                        <Grid>
                          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Grid container spacing={1}>
                              <Grid size={12} display={'flex'} flexWrap={'wrap'}>
                                  <ToggleButtonGroup
                                  exclusive
                                  fullWidth = {isMd? false: true}
                                  aria-label="filer content types"
                                  size="small"
                                  color="primary"
                                  value={contentType}
                                  onChange={switchContentType}
                                >
                                  {contentTypes.slice(0,contentTypesCutOffIndex).map((type) => (
                                    <ToggleButton 
                                      key={type} 
                                      value={type} 
                                      aria-label={`filer data - ${type}`}
                                      sx={{fontWeight:800, width: isLG && currentFilerRelatedReports?.length===0? 180: 150}}
                                    >
                                      {type}
                                    </ToggleButton>
                                  ))}
                                  </ToggleButtonGroup>
                                  <ToggleButtonGroup
                                    exclusive
                                    fullWidth = {isMd? false: true}
                                    aria-label="filer content types"
                                    size="small"
                                    color="primary"
                                    value={contentType}
                                    onChange={switchContentType}
                                  >
                                  {contentTypes.slice(contentTypesCutOffIndex).map((type) => (
                                    <ToggleButton key={type} value={type} aria-label={`filer data - ${type}`}
                                     sx={{fontWeight:800, width: isLG && currentFilerRelatedReports?.length===0? 200: 150}}
                                    >
                                      {type}
                                    </ToggleButton>
                                  ))}
                                  </ToggleButtonGroup>
                              </Grid>
                            </Grid>
                            <Box sx={{ p: 3 }}>
                              {
                                  contentType === 'Summary' &&
                                  <>
                                     <Grid container spacing={4}>
                                        <Grid size={{ xs: 12, md: 11 }}>
                                            <FilerBasics></FilerBasics>
                                        </Grid>
                                     </Grid>
                                    <FilerSummary></FilerSummary>
                                  </>
                              }
                              {
                                contentType === 'Reports' &&
                                <FilerReports></FilerReports>
                              }
                              {
                                contentType === 'Contributions' &&
                                <FilerContributions></FilerContributions>
                              }
                              {
                                contentType === 'Expenditures' &&
                                <FilerExpenditures></FilerExpenditures>
                              }
                              {
                                contentType === 'Correspondence' &&
                                <FilerDocuments></FilerDocuments>
                              }
                              {
                                contentType === 'Audit' &&
                                <FilerAudit></FilerAudit>
                              }
                              {
                                contentType === 'Related Reports' &&
                                <FilerRelatedReports></FilerRelatedReports>
                              }
                            </Box>
                          </Box>
                        </Grid>   
                       </Box>
                     </Box>   
                   </Box>
                 </Box>
                </Box>
             }
             {
                !currentFilerPayload?.filer && !isMdAndSM &&
                <Box
                  display="flex"
                  flex="1 1 auto"
                  overflow="hidden"
                  paddingLeft={{ xl: '275px', lg: '250px', md: '300px'}}
                  paddingTop={1}
                >
                  <Box
                    width={1}
                    height={1}
                    minHeight={'calc(90vh)'}
                    borderRadius={2}
                  >
                    <FilerGeneralOverview></FilerGeneralOverview>
                  </Box>
                   
                </Box>
             }
          </main>
        </Box>
        {/*loading indicator*/}
        <CircularProgress
            size={50}
            className={isLoading? "": "hidden"}
            sx={{
              position: "fixed",
              left: "50%",
              top: "30%",
              transform: "translate(-50%, -50%)",
              zIndex: 2
             }}
        />
      </FilersDataContext.Provider>
    </Main>
  );
};

export default Filers;
